import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';

import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreateWorkspaceForm from './CreateWorkspace/CreateWorkspaceForm';
import EditWorkspaces from './EditWorkspace/EditWorkspaces';
import { User, Document } from '../../../types/workspace';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { Modal } from '../../../global';
import { ModalType } from '../../../types/modal';

const WorkspacesPanel = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const axiosPrivate = useAxiosPrivate();
  const [tool, setTool] = React.useState('create');
  const [users, setUsers] = useState<User[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalInfo, setModalInfo] = useState<ModalType>({
    titleText: '',
    bodyText: '',
    autoClose: false,
  });

  useEffect(() => {
    // Fetch users and documents from the API
    const fetchData = async () => {
      setLoading(true);
      try {
        const usersResponse = await axiosPrivate.get(
          '/admin/workspace/get-users'
        );
        const documentsResponse = await axiosPrivate.get(
          '/admin/workspace/get-documents'
        );

        setUsers(usersResponse.data);
        setDocuments(documentsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [axiosPrivate]);

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string
  ) => {
    if (newAlignment !== null) {
      setTool(newAlignment);
    }
  };

  return (
    <Box
      color={theme.brandcolor.primaryDark}
      sx={{
        padding: '40px 30px',
        bgcolor: theme.brandcolor.primaryLight,
        width: '80%',
        borderRadius: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '1em',
        }}
      >
        <Button
          variant="text"
          size="large"
          sx={{ color: 'black' }}
          onClick={() => {
            navigate('/home');
          }}
          startIcon={<ArrowBackIcon />}
        >
          Home
        </Button>
        <ToggleButtonGroup
          color="primary"
          value={tool}
          exclusive
          onChange={handleChange}
          size="large"
          sx={{ flexGrow: 1, justifyContent: 'center', display: 'flex' }}
        >
          <ToggleButton value="create">Create Workspace</ToggleButton>
          <ToggleButton value="edit">Edit Workspace</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {showModal && (
        <Modal
          titleText={modalInfo.titleText}
          bodyText={modalInfo.bodyText}
          autoClose={modalInfo.autoClose}
          updateParent={setShowModal}
        />
      )}

      <Divider sx={{ mb: 3 }} />
      {tool === 'create' && (
        <CreateWorkspaceForm
          users={users}
          documents={documents}
          loading={loading}
          setShowModal={setShowModal}
          setModalInfo={setModalInfo}
        />
      )}
      {tool === 'edit' && (
        <EditWorkspaces
          users={users}
          documents={documents}
          setShowModal={setShowModal}
          setModalInfo={setModalInfo}
        />
      )}
    </Box>
  );
};

export default WorkspacesPanel;
